// src/components/APanel/QuestionEditor/ExamImportPopup.jsx
//
// Întrebări → Unelte → „Import bilete corectate” (examen), în sub-popup. Fost panoul
// de import din Corectare examen: fișierul JSON cu biletele corectate actualizează
// textele RO / RU, răspunsurile și răspunsul corect (de la 1). Biletul se găsește
// după id, număr (B12), cheie sau nume; întrebarea după id sau după poziție. Un
// fișier fără bilet (doar întrebările) se aplică pe biletul deschis în editor.

import React, { useState } from "react";

import {
   getExamTicketById,
   getExamTicketQuestions,
   updateExamQuestion,
   updateExamTicket,
} from "../../../api/examAdminService";
import {
   buildImportApplicationPlan,
   parseOfficialTicketsImport,
   resolveImportTicket,
} from "../../../utils/examTicketsOfficialImport";
import { ButtonUI, CardUI, ListUI, StatusUI } from "../../UI";
import {
   ImportLog,
   ImportWarnings,
   JsonFileButton,
   loadAllExamTickets,
   normalizeTicketDetail,
   normalizeTicketQuestions,
   readJsonFile,
   safeList,
} from "./importTools";

export default function ExamImportPopup({ currentTicketId, currentTicketLabel, onDone }) {
   const [fileName, setFileName] = useState("");
   const [bundle, setBundle] = useState(null);
   const [applying, setApplying] = useState(false);
   const [notice, setNotice] = useState(null);
   const [log, setLog] = useState([]);

   const pickFile = async (file) => {
      setLog([]);
      try {
         const parsed = await readJsonFile(file);
         if (!parsed || typeof parsed !== "object") throw new Error("Fișier JSON invalid.");

         const normalized = parseOfficialTicketsImport(parsed);
         if (!normalized.tickets.length) {
            throw new Error(normalized.errors[0] || "Fișierul nu conține bilete cu întrebări.");
         }

         setFileName(file.name);
         setBundle(normalized);

         const warning =
            normalized.summary.correctFallbackCount > 0
               ? ` · ${normalized.summary.correctFallbackCount} fără răspuns corect explicit`
               : "";
         setNotice({
            tone: normalized.errors.length || normalized.summary.correctFallbackCount ? "warning" : "success",
            text: `Import: ${normalized.summary.ticketsTotal} bilete, ${normalized.summary.questionsTotal} întrebări${warning}.`,
         });
      } catch (error) {
         setFileName("");
         setBundle(null);
         setNotice({ tone: "error", text: error?.message || "Nu am putut citi fișierul JSON." });
      }
   };

   const apply = async () => {
      if (!bundle?.tickets?.length || applying) return;

      setApplying(true);
      setNotice({ tone: "loading", text: "Se aplică importul…" });
      setLog([]);
      const rows = [];
      let appliedQuestions = 0;

      try {
         const dbTickets = await loadAllExamTickets();
         if (!dbTickets.length) throw new Error("Nu am găsit biletele de examen în backend.");

         for (const importTicket of bundle.tickets) {
            let dbTicket = resolveImportTicket(importTicket, dbTickets);

            if (!dbTicket && importTicket.ticketKey === "selected") {
               dbTicket = dbTickets.find((row) => Number(row.id) === Number(currentTicketId)) || null;
            }

            if (!dbTicket?.id) {
               rows.push({ status: "error", target: importTicket.ticketKey, message: "Biletul nu a fost găsit în backend." });
               continue;
            }

            const [ticketRaw, questionsRaw] = await Promise.all([
               getExamTicketById(dbTicket.id),
               getExamTicketQuestions(dbTicket.id),
            ]);

            const existingQuestions = normalizeTicketQuestions(questionsRaw);
            const ticketMeta = normalizeTicketDetail(ticketRaw, dbTicket, existingQuestions);
            const plan = buildImportApplicationPlan(importTicket, dbTicket, existingQuestions, ticketMeta?.questionIds || []);

            let ticketApplied = 0;
            const readyUpdates = plan.updates.filter((row) => row.status === "ready");

            for (const update of readyUpdates) {
               await updateExamQuestion(update.questionId, update.payload);
               ticketApplied += 1;
               appliedQuestions += 1;
            }

            // biletul primește întrebările în ordinea din fișier
            const nextQuestionIds = readyUpdates
               .sort((left, right) => Number(left.order) - Number(right.order))
               .map((row) => Number(row.questionId))
               .filter((id) => Number.isInteger(id) && id > 0);
            const currentIds = safeList(ticketMeta?.questionIds).map((id) => Number(id));

            if (nextQuestionIds.length && JSON.stringify(nextQuestionIds) !== JSON.stringify(currentIds)) {
               await updateExamTicket(dbTicket.id, {
                  name: String(ticketMeta?.name ?? dbTicket?.name ?? "").trim() || `Ticket ${dbTicket.id}`,
                  questionIds: nextQuestionIds,
               });
            }

            rows.push({
               status: plan.missingCount ? "warning" : "ok",
               target: `#${dbTicket.id} · ${plan.ticketName}`,
               message: plan.missingCount
                  ? `${ticketApplied} actualizate, ${plan.missingCount} lipsă.`
                  : `${ticketApplied} întrebări actualizate.`,
            });
         }

         const failed = rows.filter((row) => row.status === "error").length;
         setNotice(
            failed
               ? { tone: "warning", text: `Import parțial: ${appliedQuestions} întrebări, ${failed} bilete negăsite.` }
               : { tone: "success", text: `Import aplicat: ${appliedQuestions} întrebări actualizate.` },
         );
      } catch (error) {
         setNotice({ tone: "error", text: error?.message || "Nu am putut aplica importul." });
      } finally {
         setLog(rows);
         setApplying(false);
         if (appliedQuestions > 0) onDone?.();
      }
   };

   const hasSelected = bundle?.tickets?.some((ticket) => ticket.ticketKey === "selected");

   return (
      <CardUI
         level="popup"
         title="Import bilete corectate"
         description="Examen. Se actualizează textele RO / RU, răspunsurile și răspunsul corect; biletul primește întrebările în ordinea din fișier."
      >
         <ButtonUI.Row align="start">
            <JsonFileButton onFile={pickFile} disabled={applying} />
            <ButtonUI
               variant="primary"
               confirm="Da, aplică"
               confirmTitle={
                  bundle ? `${bundle.tickets.length} bilete, ${bundle.summary.questionsTotal} întrebări` : undefined
               }
               loading={applying}
               disabled={!bundle?.tickets?.length || applying}
               onClick={apply}
            >
               Aplică importul
            </ButtonUI>
         </ButtonUI.Row>

         {bundle && (
            <ListUI columns={3}>
               <ListUI.Item layout="stat" title="Bilete" meta={bundle.summary.ticketsTotal} />
               <ListUI.Item layout="stat" title="Întrebări" meta={bundle.summary.questionsTotal} />
               <ListUI.Item
                  layout="stat"
                  title="Fără răspuns corect"
                  meta={bundle.summary.correctFallbackCount}
                  tone={bundle.summary.correctFallbackCount ? "bad" : undefined}
               />
            </ListUI>
         )}

         <StatusUI tone={fileName ? "neutral" : "empty"}>
            {fileName
               ? `Fișier: ${fileName}`
               : "Formatul: { bilete | tickets: [{ ticket: \"B12\", questions: [...] }] } sau doar lista întrebărilor."}
         </StatusUI>

         {hasSelected && (
            <StatusUI look="card" tone={currentTicketId ? "info" : "warning"}>
               {currentTicketId
                  ? `Întrebările fără bilet se aplică pe biletul deschis: ${currentTicketLabel || `#${currentTicketId}`}.`
                  : "Întrebările fără bilet nu au unde merge: deschide întâi biletul în editor."}
            </StatusUI>
         )}

         <ImportWarnings items={bundle?.errors} />

         {notice && <StatusUI tone={notice.tone}>{notice.text}</StatusUI>}

         <ImportLog rows={log} />
      </CardUI>
   );
}
